<template>
  <div class="voice-settings-page page">
    <div class="page-header">
      <div class="text-lg font-bold text-white">{{ $t('voiceSettings.title') }}</div>
      <div class="header-actions">
        <el-button type="primary" @click="handleAddVoice" class="add-voice-btn">
          <i class="el-icon-plus mr-1"></i>
          {{ $t('voiceSettings.addVoice') }}
        </el-button>
      </div>
    </div>

    <!-- 区域切换优化 -->
    <div class="region-tabs mb-6">
      <div 
        class="tab-item" 
        :class="{ active: groupId === 1 }"
        @click="groupId = 1; fetchVoiceList()"
      >
        <i class="el-icon-office-building mr-2"></i>
        <span>{{ $t('voiceSettings.domestic') || '国内音色' }}</span>
      </div>
      <div 
        class="tab-item" 
        :class="{ active: groupId === 2 }"
        @click="groupId = 2; fetchVoiceList()"
      >
        <i class="el-icon-connection mr-2"></i>
        <span>{{ $t('voiceSettings.international') || '国外音色' }}</span>
      </div>
      <div class="tab-slider" :style="{ transform: `translateX(${groupId === 1 ? '0' : '100%'})` }"></div>
    </div>

    <!-- 数据表格容器 -->
    <div class="content-container flex-1 bg-gray-700 rounded-xl p-4 overflow-hidden flex flex-col">
      <div class="table-header mb-4 flex justify-between items-center pb-3">
        <span class="text-white text-base font-medium flex items-center">
          <i class="el-icon-microphone mr-2 header-icon"></i>
          {{ $t('voiceSettings.voiceList') }}
        </span>
        <span class="text-gray-400 text-sm font-normal">{{ $t('voiceSettings.totalCount', { count: voiceList.length }) }}</span>
      </div>
      
      <div class="flex-1 overflow-hidden relative">
        <el-table 
          :data="voiceList" 
          v-loading="loading" 
          height="100%"
          style="width: 100%"
          :empty-text="loading ? $t('common.loading') : $t('common.noData')"
          class="custom-dark-table"
          stripe
          :header-cell-style="{
            background: '#404761',
            color: '#fff',
            borderBottom: '1px solid #4A4A6A',
            fontWeight: '500',
            fontSize: '14px'
          }"
          :cell-style="{
            background: 'transparent',
            color: '#E5E7EB',
            borderBottom: '1px solid #4A4A6A',
            fontSize: '14px'
          }"
          :row-style="{ background: 'transparent' }"
        >
          <el-table-column type="index" width="60" label="#" align="center" />
          <el-table-column 
            prop="voiceId" 
            :label="$t('voiceSettings.voiceId')" 
            min-width="150" 
            align="center"
            show-overflow-tooltip
          />
          <el-table-column 
            prop="voiceName" 
            :label="$t('voiceSettings.voiceName')" 
            min-width="150" 
            align="center"
            show-overflow-tooltip
          />
          <el-table-column 
            prop="gender" 
            :label="$t('voiceSettings.gender')" 
            width="100" 
            align="center"
          >
            <template slot-scope="scope">
              <span>{{ getGenderLabel(scope.row.gender) }}</span>
            </template>
          </el-table-column>
          <el-table-column 
            prop="model" 
            :label="$t('voiceSettings.model')" 
            min-width="120" 
            align="center"
            show-overflow-tooltip
          />
          <el-table-column 
            prop="speechRate" 
            :label="$t('voiceSettings.speechRate')" 
            width="120" 
            align="center"
          >
            <template slot-scope="scope">
              <span>{{ scope.row.speechRate || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column 
            prop="pitchRate" 
            :label="$t('voiceSettings.pitchRate')" 
            width="120" 
            align="center"
          >
            <template slot-scope="scope">
              <span>{{ scope.row.pitchRate || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column 
            :label="$t('voiceSettings.operation')" 
            width="180" 
            align="center"
            fixed="right"
          >
            <template slot-scope="scope">
              <el-button 
                type="text" 
                size="small" 
                class="edit-btn"
                @click="handleEditVoice(scope.row)"
              >
                {{ $t('common.edit') }}
              </el-button>
              <el-button 
                type="text" 
                size="small" 
                class="delete-btn"
                @click="handleDeleteVoice(scope.row)"
              >
                {{ $t('voiceSettings.delete') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 添加音色对话框 -->
    <el-dialog
      :title="$t('voiceSettings.addVoice')"
      :visible.sync="addVoiceDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      custom-class="dark-dialog"
      :append-to-body="true"
    >
      <el-form 
        ref="addVoiceForm" 
        :model="addVoiceForm" 
        :rules="addVoiceRules" 
        label-width="120px"
        class="add-voice-form"
      >
        <el-form-item :label="$t('voiceSettings.voiceName')" prop="voiceName">
          <el-input
            v-model="addVoiceForm.voiceName"
            :placeholder="$t('voiceSettings.voiceNamePlaceholder')"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>

        <el-form-item :label="$t('voiceSettings.region') || '区域'" prop="groupId">
          <el-select v-model="addVoiceForm.groupId" placeholder="请选择区域" style="width: 100%">
            <el-option label="国内" :value="1" />
            <el-option label="国外" :value="2" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('createRole.language')" prop="language">
          <el-select
            v-model="addVoiceForm.language"
            :placeholder="$t('createRole.selectLanguage')"
            style="width: 100%"
          >
            <el-option
              v-for="lang in languageOptions"
              :key="lang.value"
              :label="lang.label"
              :value="lang.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('voiceSettings.addMethod')" prop="addMode">
          <el-radio-group 
            v-model="addVoiceForm.addMode" 
            size="small" 
            @change="handleAddModeChange"
            class="compact-radio-group"
          >
            <el-radio-button label="upload">
              <i class="el-icon-upload mr-1"></i>
              {{ $t('voiceSettings.uploadAudio') }}
            </el-radio-button>
            <el-radio-button label="manual">
              <i class="el-icon-edit-outline mr-1"></i>
              {{ $t('voiceSettings.manualVoiceId') }}
            </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          v-if="addVoiceForm.addMode === 'upload'"
          :label="$t('voiceSettings.audioFile')"
          prop="fileUrl"
        >
          <el-upload
            ref="audioUploadRef"
            class="upload-audio-container"
            drag
            :http-request="handleAudioUploadRequest"
            :show-file-list="false"
            :before-upload="beforeAudioUpload"
            accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
            style="display: none;"
          >
          </el-upload>
          <div 
            v-if="addVoiceForm.fileUrl" 
            class="uploaded-audio-area"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <i class="el-icon-document text-white text-2xl mr-3"></i>
                <span class="text-white text-sm">{{ $t('voiceSettings.uploaded') }}</span>
              </div>
              <el-button 
                size="small" 
                type="text" 
                class="reupload-btn"
                @click="handleReuploadAudio"
              >
                <i class="el-icon-upload2 mr-1"></i>
                {{ $t('voiceSettings.reupload') }}
              </el-button>
            </div>
            <el-progress
              v-if="audioUploading"
              :percentage="audioUploadProgress"
              :status="audioUploadProgress === 100 ? 'success' : null"
              :stroke-width="6"
              class="mt-3"
            ></el-progress>
          </div>
          <div
            v-else
            class="upload-audio-area"
            @click="handleClickUpload"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
          >
            <i class="el-icon-upload2 text-4xl text-gray-400 mb-2"></i>
            <div class="text-gray-400 text-sm">{{ $t('voiceSettings.dragOrClick') }}</div>
            <div class="text-gray-400 text-xs mt-2">{{ $t('voiceSettings.uploadDurationHint', { seconds: maxAudioDurationSeconds }) }}</div>
            <div v-if="audioUploading" class="mt-3 w-full">
              <el-progress
                :percentage="audioUploadProgress"
                :status="audioUploadProgress === 100 ? 'success' : null"
                :stroke-width="6"
              ></el-progress>
            </div>
          </div>
        </el-form-item>

        <el-form-item
          v-else
          :label="$t('voiceSettings.voiceId')"
          prop="voiceId"
        >
          <el-input
            v-model="addVoiceForm.voiceId"
            :placeholder="$t('voiceSettings.voiceIdPlaceholder') || '请输入音色ID，例如：longxiaochun'"
            maxlength="100"
          />
        </el-form-item>


      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleCancelAddVoice">{{ $t('common.cancel') }}</el-button>
        <el-button 
          type="primary" 
          @click="handleSubmitAddVoice" 
          :loading="savingVoice"
          class="submit-btn"
        >
          {{ $t('common.save') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 编辑音色对话框 -->
    <el-dialog
      :title="$t('voiceSettings.editVoice')"
      :visible.sync="editVoiceDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      custom-class="dark-dialog"
      :append-to-body="true"
    >
      <el-form 
        ref="editVoiceForm" 
        :model="editVoiceForm" 
        :rules="editVoiceRules" 
        label-width="120px"
        class="edit-voice-form"
      >
        <el-form-item :label="$t('voiceSettings.voiceName')" prop="voiceName">
          <el-input
            v-model="editVoiceForm.voiceName"
            :placeholder="$t('voiceSettings.voiceNamePlaceholder')"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>

        <el-form-item :label="$t('voiceSettings.region') || '区域'" prop="groupId">
          <el-select v-model="editVoiceForm.groupId" placeholder="请选择区域" style="width: 100%">
            <el-option label="国内" :value="1" />
            <el-option label="国外" :value="2" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('voiceSettings.gender')" prop="gender" class="gender-form-item">
          <el-radio-group v-model="editVoiceForm.gender" class="gender-radio-group">
            <el-radio :label="0">{{ $t('voiceSettings.female') }}</el-radio>
            <el-radio :label="1">{{ $t('voiceSettings.male') }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item :label="$t('voiceSettings.speechRateLabel')" prop="speechRate">
          <div class="slider-container">
            <el-slider
              v-model="editVoiceForm.speechRate"
              :min="0.5"
              :max="1.5"
              :step="0.1"
              :show-tooltip="true"
              class="speech-rate-slider"
            ></el-slider>
          </div>
          <div class="flex justify-between mt-1">
            <span class="text-gray-400 text-xs">0.5x</span>
            <span class="text-gray-400 text-xs">{{ editVoiceForm.speechRate.toFixed(1) }}x</span>
            <span class="text-gray-400 text-xs">1.5x</span>
          </div>
        </el-form-item>

        <el-form-item :label="$t('voiceSettings.pitchRateLabel')" prop="pitchRate">
          <div class="slider-container">
            <el-slider
              v-model="editVoiceForm.pitchRate"
              :min="0.5"
              :max="1.5"
              :step="0.1"
              :show-tooltip="true"
              class="pitch-rate-slider"
            ></el-slider>
          </div>
          <div class="flex justify-between mt-1">
            <span class="text-gray-400 text-xs">0.5</span>
            <span class="text-gray-400 text-xs">{{ editVoiceForm.pitchRate.toFixed(1) }}</span>
            <span class="text-gray-400 text-xs">1.5</span>
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleCancelEditVoice">{{ $t('common.cancel') }}</el-button>
        <el-button 
          type="primary" 
          @click="handleSubmitEditVoice" 
          :loading="savingEditVoice"
          class="submit-btn"
        >
          {{ $t('common.save') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import voiceApi from '@/network/voice/index'

const MAX_AUDIO_DURATION_SECONDS = 128

export default {
  name: 'VoiceSettings',
  data() {
    return {
      groupId: 1,
      loading: false,
      voiceList: [],
      addVoiceDialogVisible: false,
      addVoiceForm: {
        voiceName: '',
        fileUrl: '',
        voiceId: '',
        addMode: 'upload',
        groupId: 1,
        language: ''
      },
      maxAudioDurationSeconds: MAX_AUDIO_DURATION_SECONDS,
      audioUploading: false,
      audioUploadProgress: 0,
      savingVoice: false,
      // 编辑相关
      editVoiceDialogVisible: false,
      editVoiceForm: {
        id: null,
        voiceName: '',
        gender: 0,
        speechRate: 1.0,
        pitchRate: 1.0,
        groupId: 1
      },
      savingEditVoice: false
    }
  },
  computed: {
    addVoiceRules() {
      const validateFileUrl = (rule, value, callback) => {
        if (this.addVoiceForm.addMode === 'upload' && !value) {
          callback(new Error(this.$t('voiceSettings.audioFileRequired')))
          return
        }
        callback()
      }
      const validateVoiceId = (rule, value, callback) => {
        if (this.addVoiceForm.addMode === 'manual' && !(value || '').trim()) {
          callback(new Error(this.$t('voiceSettings.voiceIdRequired') || '请输入音色ID'))
          return
        }
        callback()
      }
      return {
        voiceName: [
          { required: true, message: this.$t('voiceSettings.voiceNameRequired'), trigger: 'blur' },
          { max: 50, message: this.$t('voiceSettings.voiceNameMaxLength'), trigger: 'blur' }
        ],
        language: [
          { required: true, message: this.$t('createRole.languageRequired'), trigger: 'change' }
        ],
        addMode: [
          { required: true, message: this.$t('voiceSettings.addMethodRequired'), trigger: 'change' }
        ],
        fileUrl: [
          { validator: validateFileUrl, trigger: 'change' }
        ],
        voiceId: [
          { validator: validateVoiceId, trigger: 'blur' }
        ]
      }
    },
    languageOptions() {
      return [
        { label: this.$t('createRole.languageChinese'), value: 'zh' },
        { label: this.$t('createRole.languageCantonese'), value: 'yue' },
        { label: this.$t('createRole.languageJapanese'), value: 'ja' },
        { label: this.$t('createRole.languageKorean'), value: 'ko' },
        { label: this.$t('createRole.languageEnglish'), value: 'en' },
        { label: this.$t('createRole.languageFrench'), value: 'fr' },
        { label: this.$t('createRole.languageSpanish'), value: 'es' },
        { label: this.$t('createRole.languageItalian'), value: 'it' },
        { label: this.$t('createRole.languageGerman'), value: 'de' }
      ]
    },
    editVoiceRules() {
      return {
        voiceName: [
          { required: true, message: this.$t('voiceSettings.voiceNameRequired'), trigger: 'blur' },
          { max: 50, message: this.$t('voiceSettings.voiceNameMaxLength') || '名称最多50个字符', trigger: 'blur' }
        ],
        gender: [
          { required: true, message: '请选择性别', trigger: 'change' }
        ]
      }
    }
  },
  mounted() {
    this.fetchVoiceList()
  },
  methods: {
    // 获取音色列表
    async fetchVoiceList() {
      this.loading = true
      try {
        const res = await voiceApi.getSystemVoices({ groupId: this.groupId })
        if (res.statusCode === 100200) {
          this.voiceList = res.data || []
        } else {
          this.$message.error(this.$t('voiceSettings.loadError'))
        }
      } catch (err) {
        console.error('获取音色列表失败:', err)
        if (err.isBusinessError) {
          this.$message.error(this.$t('voiceSettings.loadError'))
        } else {
          this.$message.error(this.$t('voiceSettings.networkError'))
        }
      } finally {
        this.loading = false
      }
    },
    // 获取性别标签
    getGenderLabel(gender) {
      if (gender === 0) return this.$t('voiceSettings.female')
      if (gender === 1) return this.$t('voiceSettings.male')
      return '—'
    },
    // 添加音色
    handleAddVoice() {
      this.addVoiceDialogVisible = true
      this.resetAddVoiceForm()
    },
    // 重置添加音色表单
    resetAddVoiceForm() {
      this.addVoiceForm = {
        voiceName: '',
        fileUrl: '',
        voiceId: '',
        addMode: 'upload',
        groupId: this.groupId,
        language: ''
      }
      this.audioUploading = false
      this.audioUploadProgress = 0
      this.$nextTick(() => {
        if (this.$refs.addVoiceForm) {
          this.$refs.addVoiceForm.clearValidate()
        }
      })
    },
    // 取消添加音色
    handleCancelAddVoice() {
      this.addVoiceDialogVisible = false
      this.resetAddVoiceForm()
    },
    // 提交添加音色 - 使用 /voice/system/createVoice 接口
    async handleSubmitAddVoice() {
      this.$refs.addVoiceForm.validate(async (valid) => {
        if (!valid) return

        this.savingVoice = true
        try {
          // 调用 voiceApi.createSystemVoice，内部使用 /voice/system/createVoice 接口
          // 参数通过 @RequestParam 传递：voiceName, fileUrl, speechRate (Double), pitchRate (Double)
          const isManualAdd = this.addVoiceForm.addMode === 'manual'
          const res = await voiceApi.createSystemVoice({
            voiceName: this.addVoiceForm.voiceName.trim(),
            fileUrl: isManualAdd ? '' : this.addVoiceForm.fileUrl,
            voiceId: isManualAdd ? this.addVoiceForm.voiceId.trim() : '',
            speechRate: 1.0,
            pitchRate: 1.0,
            groupId: this.addVoiceForm.groupId,
            language: this.addVoiceForm.language
          })

          if (res.statusCode === 100200) {
            this.$message.success(this.$t('voiceSettings.addVoiceSuccess'))
            this.addVoiceDialogVisible = false
            this.resetAddVoiceForm()
            // 刷新列表
            this.fetchVoiceList()
          } else {
            this.$message.error(res.message || this.$t('voiceSettings.addVoiceFailed'))
          }
        } catch (error) {
          console.error('添加音色失败:', error)
          const errorMsg = error.message || error.response?.data?.message || this.$t('voiceSettings.addVoiceFailed')
          this.$message.error(errorMsg)
        } finally {
          this.savingVoice = false
        }
      })
    },
    // 添加方式切换
    handleAddModeChange(mode) {
      if (mode === 'upload') {
        this.addVoiceForm.voiceId = ''
      } else {
        this.addVoiceForm.fileUrl = ''
        this.audioUploadProgress = 0
        this.audioUploading = false
      }
      this.$nextTick(() => {
        this.$refs.addVoiceForm?.clearValidate(['fileUrl', 'voiceId'])
      })
    },
    // 音频上传前验证
    async beforeAudioUpload(file) {
      const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/aac', 'audio/ogg', 'audio/flac']
      const validExtensions = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac']
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
      
      const isValidType = validTypes.includes(file.type) || validExtensions.some(ext => 
        file.name.toLowerCase().endsWith(ext)
      )
      
      if (!isValidType) {
        this.$message.error(this.$t('voiceSettings.audioFormatError'))
        return false
      }
      
      const maxSize = 20 * 1024 * 1024 // 20MB
      if (file.size > maxSize) {
        this.$message.error(this.$t('voiceSettings.audioSizeError'))
        return false
      }

      const isDurationValid = await this.validateAudioDuration(file)
      if (!isDurationValid) {
        return false
      }
      
      return true
    },
    // 上传前校验音频时长
    validateAudioDuration(file) {
      return new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file)
        const audio = new Audio()
        audio.preload = 'metadata'
        audio.src = objectUrl

        audio.onloadedmetadata = () => {
          const duration = Number(audio.duration || 0)
          URL.revokeObjectURL(objectUrl)
          if (!Number.isFinite(duration) || duration <= 0) {
            this.$message.error(this.$t('voiceSettings.audioDurationReadFailed'))
            resolve(false)
            return
          }
          if (duration > MAX_AUDIO_DURATION_SECONDS) {
            this.$message.error(this.$t('voiceSettings.audioDurationLimitExceeded', { seconds: MAX_AUDIO_DURATION_SECONDS }))
            resolve(false)
            return
          }
          resolve(true)
        }

        audio.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          this.$message.error(this.$t('voiceSettings.audioDurationReadFailed'))
          resolve(false)
        }
      })
    },
    // 处理音频上传 - 使用 /voice/uploadAudio 接口
    async handleAudioUploadRequest(options) {
      const { file } = options
      this.audioUploading = true
      this.audioUploadProgress = 0
      
      try {
        // 调用 voiceApi.uploadAudio，内部使用 /voice/uploadAudio 接口
        const res = await voiceApi.uploadAudio(file, (percent) => {
          this.audioUploadProgress = percent
        })
        
        this.audioUploading = false
        if (res.statusCode === 100200) {
          const audioUrl = res.data?.url || res.data
          this.addVoiceForm.fileUrl = audioUrl
          this.$message.success(this.$t('voiceSettings.uploadSuccess'))
          // 触发表单验证
          this.$nextTick(() => {
            this.$refs.addVoiceForm?.validateField('fileUrl')
          })
          setTimeout(() => {
            this.audioUploadProgress = 0
          }, 1000)
        } else {
          this.$message.error(res.message || this.$t('voiceSettings.uploadFailed'))
          this.audioUploadProgress = 0
        }
      } catch (error) {
        this.audioUploading = false
        this.audioUploadProgress = 0
        const errorMsg = error.message || error.response?.data?.message || this.$t('voiceSettings.uploadFailed')
        this.$message.error(errorMsg)
      }
    },
    // 重新上传音频
    handleReuploadAudio() {
      this.addVoiceForm.fileUrl = ''
      this.$nextTick(() => {
        const input = this.$refs.audioUploadRef?.$el?.querySelector('input[type="file"]')
        if (input) {
          input.click()
        }
      })
    },
    // 点击上传区域
    handleClickUpload() {
      const input = this.$refs.audioUploadRef?.$el?.querySelector('input[type="file"]')
      if (input) {
        input.click()
      }
    },
    // 拖拽相关处理
    handleDragOver(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    handleDragLeave(e) {
      e.preventDefault()
      e.stopPropagation()
    },
    async handleDrop(e) {
      e.preventDefault()
      e.stopPropagation()
      
      const files = e.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        if (await this.beforeAudioUpload(file)) {
          this.handleAudioUploadRequest({ file })
        }
      }
    },
    // 编辑音色
    handleEditVoice(item) {
      this.editVoiceForm = {
        id: item.id,
        voiceName: item.voiceName || '',
        gender: item.gender !== undefined ? item.gender : 0,
        speechRate: item.speechRate !== undefined ? item.speechRate : 1.0,
        pitchRate: item.pitchRate !== undefined ? item.pitchRate : 1.0,
        groupId: item.groupId || this.groupId
      }
      this.editVoiceDialogVisible = true
      this.resetEditVoiceForm()
    },
    // 重置编辑音色表单
    resetEditVoiceForm() {
      this.$nextTick(() => {
        if (this.$refs.editVoiceForm) {
          this.$refs.editVoiceForm.clearValidate()
        }
      })
    },
    // 取消编辑音色
    handleCancelEditVoice() {
      this.editVoiceDialogVisible = false
      this.resetEditVoiceForm()
    },
    // 提交编辑音色
    async handleSubmitEditVoice() {
      this.$refs.editVoiceForm.validate(async (valid) => {
        if (!valid) return

        this.savingEditVoice = true
        try {
          const res = await voiceApi.updateSystemVoice({
            id: this.editVoiceForm.id,
            voiceName: this.editVoiceForm.voiceName.trim(),
            gender: this.editVoiceForm.gender,
            speechRate: this.editVoiceForm.speechRate,
            pitchRate: this.editVoiceForm.pitchRate,
            groupId: this.editVoiceForm.groupId
          })
          
          if (res.statusCode === 100200) {
            this.$message.success(this.$t('voiceSettings.editVoiceSuccess') || '编辑成功')
            this.editVoiceDialogVisible = false
            this.resetEditVoiceForm()
            this.fetchVoiceList()
          } else {
            this.$message.error(res.message || this.$t('voiceSettings.editVoiceFailed') || '编辑失败')
          }
        } catch (error) {
          console.error('编辑音色失败:', error)
          const errorMsg = error.message || error.response?.data?.message || this.$t('voiceSettings.editVoiceFailed') || '编辑失败'
          this.$message.error(errorMsg)
        } finally {
          this.savingEditVoice = false
        }
      })
    },
    // 删除音色
    async handleDeleteVoice(item) {
      try {
        await this.$confirm(
          this.$t('voiceSettings.deleteConfirm'), 
          this.$t('voiceSettings.tip'), 
          {
            confirmButtonText: this.$t('voiceSettings.confirm'),
            cancelButtonText: this.$t('voiceSettings.cancel'),
            type: 'warning'
          }
        )
        
        const res = await voiceApi.deleteSystemVoice({
          id: item.id,
          groupId: item.groupId
        })
        
        if (res.statusCode === 100200) {
          this.$message.success(this.$t('voiceSettings.deleteSuccess'))
          // 重新加载列表
          await this.fetchVoiceList()
        } else {
          this.$message.error(res.message || this.$t('voiceSettings.deleteFailed'))
        }
      } catch (error) {
        if (error !== 'cancel') {
          const errorMsg = error.message || error.response?.data?.message || this.$t('common.unknownError')
          this.$message.error(this.$t('voiceSettings.deleteFailed') + ': ' + errorMsg)
        }
      }
    }
  }
}
</script>

<style scoped>
.voice-settings-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  padding-top: 0px;
  background-color: #232a3e;
  color: #fff;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.add-voice-btn {
  background-color: #6A33F0 !important;
  border-color: #6A33F0 !important;
  color: #fff !important;
  transition: all 0.2s ease;
}

.add-voice-btn:hover {
  background-color: #7b47ff !important;
  border-color: #7b47ff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(106, 51, 240, 0.3);
}

.add-voice-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(106, 51, 240, 0.3);
}


/* 表格容器样式 */
.content-container {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.content-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.table-header {
  border-bottom: 1px solid #4A4A6A;
}

.header-icon {
  color: #6A33F0;
  font-size: 18px;
  transition: color 0.2s ease;
}

/* Custom Scrollbar for the table container */
.content-container ::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.content-container ::-webkit-scrollbar-track {
  background: #333A53;
  border-radius: 3px;
}
.content-container ::-webkit-scrollbar-thumb {
  background: #6A33F0;
  border-radius: 3px;
  transition: background 0.2s ease;
}
.content-container ::-webkit-scrollbar-thumb:hover {
  background: #7b47ff;
}

/* Table overrides */
.custom-dark-table {
  background-color: transparent !important;
  border: none !important;
}

.custom-dark-table::before {
  display: none !important;
}

/* 表格头部样式 */
::v-deep .custom-dark-table .el-table__header-wrapper {
  background-color: transparent;
}

::v-deep .custom-dark-table .el-table__header {
  background-color: transparent;
}

::v-deep .custom-dark-table .el-table__header th {
  background-color: #404761 !important;
  color: #fff !important;
  border-bottom: 1px solid #4A4A6A !important;
  padding: 14px 10px;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

::v-deep .custom-dark-table .el-table__header th:hover {
  background-color: #4A5568 !important;
}

/* 表格主体样式 */
::v-deep .custom-dark-table .el-table__body-wrapper {
  background-color: transparent;
}

::v-deep .custom-dark-table .el-table__body td {
  background-color: transparent !important;
  color: #E5E7EB !important;
  border-bottom: 1px solid #4A4A6A !important;
  padding: 14px 10px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* 斑马纹样式 */
::v-deep .custom-dark-table .el-table__body tr.el-table__row--striped td {
  background-color: rgba(64, 71, 97, 0.3) !important;
}

/* 悬停行样式 */
::v-deep .custom-dark-table .el-table__body tr {
  transition: background-color 0.2s ease;
}

::v-deep .custom-dark-table .el-table__body tr:hover > td {
  background-color: rgba(106, 51, 240, 0.15) !important;
  color: #fff !important;
  cursor: pointer;
}

/* 表格边框 */
::v-deep .custom-dark-table .el-table td,
::v-deep .custom-dark-table .el-table th {
  border-right: none !important;
}

::v-deep .custom-dark-table .el-table--border::after,
::v-deep .custom-dark-table .el-table--group::after,
::v-deep .custom-dark-table .el-table::before {
  background-color: transparent !important;
}

/* 空状态样式 */
::v-deep .custom-dark-table .el-table__empty-block {
  background-color: transparent;
}

::v-deep .custom-dark-table .el-table__empty-text {
  color: #9CA3AF;
}

/* 加载遮罩样式 */
::v-deep .custom-dark-table .el-loading-mask {
  background-color: rgba(35, 42, 62, 0.8) !important;
}

::v-deep .custom-dark-table .el-loading-spinner {
  color: #6A33F0;
}

::v-deep .custom-dark-table .el-loading-spinner .path {
  stroke: #6A33F0;
}

/* Dialog dark theme */
::v-deep .dark-dialog {
  background-color: #333A53 !important;
  border-radius: 12px;
  border: 1px solid #4A4A6A;
}

::v-deep .dark-dialog .el-dialog__header {
  background-color: #404761;
  padding: 20px 20px 15px;
  border-bottom: 1px solid #4A4A6A;
  border-radius: 12px 12px 0 0;
}

::v-deep .dark-dialog .el-dialog__title {
  color: #fff !important;
  font-size: 16px;
  font-weight: 500;
}

::v-deep .dark-dialog .el-dialog__body {
  background-color: #333A53;
  color: #E5E7EB;
  padding: 20px;
}

::v-deep .dark-dialog .el-dialog__close {
  color: #9CA3AF !important;
  font-size: 20px;
  transition: color 0.2s ease;
}

::v-deep .dark-dialog .el-dialog__close:hover {
  color: #fff !important;
}

/* 添加音色表单样式 */
.add-voice-form {
  padding: 10px 0;
}

.upload-audio-container {
  width: 100%;
}

.upload-audio-area {
  width: 100%;
  min-height: 120px;
  border: 2px dashed #4A4A6A;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #404761;
}

.upload-audio-area:hover {
  border-color: #6A33F0;
  background-color: rgba(106, 51, 240, 0.1);
}

.uploaded-audio-area {
  width: 100%;
  padding: 16px;
  background-color: #404761;
  border-radius: 8px;
  border: 1px solid #4A4A6A;
}

.reupload-btn {
  color: #6A33F0 !important;
}

.reupload-btn:hover {
  color: #7b47ff !important;
}

.slider-container {
  width: 100%;
  padding: 0 10px;
}

/* 滑块样式 */
::v-deep .speech-rate-slider .el-slider__runway,
::v-deep .pitch-rate-slider .el-slider__runway {
  height: 6px;
  background-color: #4A4A6A;
  border-radius: 3px;
}

::v-deep .speech-rate-slider .el-slider__bar,
::v-deep .pitch-rate-slider .el-slider__bar {
  height: 6px;
  background-color: #6A33F0;
  border-radius: 3px;
}

::v-deep .speech-rate-slider .el-slider__button,
::v-deep .pitch-rate-slider .el-slider__button {
  width: 18px;
  height: 18px;
  background-color: #6A33F0;
  border: 2px solid #fff;
  border-radius: 50%;
}

::v-deep .speech-rate-slider .el-slider__button:hover,
::v-deep .pitch-rate-slider .el-slider__button:hover {
  background-color: #7b47ff;
}

::v-deep .add-voice-form .el-form-item__label {
  color: #E5E7EB;
}

::v-deep .add-voice-form .el-input__inner {
  background-color: #404761;
  border-color: #4A4A6A;
  color: #fff;
}

::v-deep .add-voice-form .el-input__inner:focus {
  border-color: #6A33F0;
}

.submit-btn {
  background-color: #6A33F0 !important;
  border-color: #6A33F0 !important;
}

.submit-btn:hover {
  background-color: #7b47ff !important;
  border-color: #7b47ff !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .voice-settings-page {
    padding: 12px;
  }

  .content-container {
    padding: 12px;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  ::v-deep .custom-dark-table .el-table__header th,
  ::v-deep .custom-dark-table .el-table__body td {
    padding: 10px 8px;
    font-size: 13px;
  }
}

/* Tag 标签样式优化 */
::v-deep .custom-dark-table .el-tag {
  border: none;
  font-weight: 500;
}

::v-deep .custom-dark-table .el-tag--dark {
  background-color: rgba(255, 255, 255, 0.1);
}

/* 序号列样式 */
::v-deep .custom-dark-table .el-table__body td:first-child,
::v-deep .custom-dark-table .el-table__header th:first-child {
  color: #9CA3AF;
  font-weight: 500;
}

/* 操作按钮样式 */
.edit-btn {
  color: #6A33F0 !important;
  font-size: 14px;
  padding: 0 8px;
  transition: all 0.2s ease;
}

.edit-btn:hover {
  color: #7b47ff !important;
}

.delete-btn {
  color: #F56565 !important;
  font-size: 14px;
  padding: 0 8px;
  transition: all 0.2s ease;
}

.delete-btn:hover {
  color: #FC8181 !important;
}

/* 编辑表单样式 */
.edit-voice-form {
  padding: 10px 0;
}

::v-deep .edit-voice-form .el-form-item__label {
  color: #E5E7EB;
}

::v-deep .edit-voice-form .el-input__inner {
  background-color: #404761;
  border-color: #4A4A6A;
  color: #fff;
}

::v-deep .edit-voice-form .el-input__inner:focus {
  border-color: #6A33F0;
}

/* 字符计数器样式 */
::v-deep .edit-voice-form .el-input__count {
  background-color: transparent !important;
  color: #9CA3AF !important;
  line-height: 1;
}

::v-deep .edit-voice-form .el-input__count-inner {
  background-color: transparent !important;
  color: #9CA3AF !important;
}

/* 性别选项对齐 */
::v-deep .edit-voice-form .gender-form-item {
  margin-bottom: 22px;
}

::v-deep .edit-voice-form .gender-form-item .el-form-item__label {
  line-height: 32px;
  padding-bottom: 0;
}

::v-deep .edit-voice-form .gender-form-item .el-form-item__content {
  display: flex;
  align-items: center;
  line-height: 32px;
}

::v-deep .edit-voice-form .gender-radio-group {
  display: flex;
  gap: 20px;
  align-items: center;
  line-height: 32px;
}

::v-deep .edit-voice-form .el-radio {
  color: #E5E7EB;
  margin-right: 0;
  line-height: 32px;
  display: flex;
  align-items: center;
  height: 32px;
}

::v-deep .edit-voice-form .el-radio__input {
  line-height: 32px;
  vertical-align: middle;
}

::v-deep .edit-voice-form .el-radio__input.is-checked .el-radio__inner {
  background-color: #6A33F0;
  border-color: #6A33F0;
}

::v-deep .edit-voice-form .el-radio__input.is-checked + .el-radio__label {
  color: #6A33F0;
}

::v-deep .edit-voice-form .slider-container {
  padding: 10px 0;
}

::v-deep .edit-voice-form .speech-rate-slider .el-slider__runway,
::v-deep .edit-voice-form .pitch-rate-slider .el-slider__runway {
  height: 6px;
  background-color: #4A4A6A;
  border-radius: 3px;
}

::v-deep .edit-voice-form .speech-rate-slider .el-slider__bar,
::v-deep .edit-voice-form .pitch-rate-slider .el-slider__bar {
  height: 6px;
  background-color: #6A33F0;
  border-radius: 3px;
}

::v-deep .edit-voice-form .speech-rate-slider .el-slider__button,
::v-deep .edit-voice-form .pitch-rate-slider .el-slider__button {
  width: 18px;
  height: 18px;
  background-color: #6A33F0;
  border: 2px solid #fff;
  border-radius: 50%;
}

::v-deep .edit-voice-form .speech-rate-slider .el-slider__button:hover,
::v-deep .edit-voice-form .pitch-rate-slider .el-slider__button:hover {
  background-color: #7b47ff;
}

/* 区域切换 Tab 样式 */
.region-tabs {
  display: flex;
  background-color: #333A53;
  padding: 4px;
  border-radius: 12px;
  position: relative;
  width: fit-content;
  border: 1px solid #4A4A6A;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  margin-top: 8px;
}

.tab-item {
  position: relative;
  z-index: 2;
  padding: 10px 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
  user-select: none;
}

.tab-item.active {
  color: #fff;
}

.tab-item:hover:not(.active) {
  color: #E5E7EB;
}

.tab-slider {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background: linear-gradient(135deg, #6A33F0 0%, #7b47ff 100%);
  border-radius: 8px;
  z-index: 1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 10px rgba(106, 51, 240, 0.4);
}

/* 紧凑型单选按钮组样式 */
.compact-radio-group {
  display: flex;
  background-color: #333A53;
  padding: 3px;
  border-radius: 8px;
  border: 1px solid #4A4A6A;
  width: fit-content;
}

::v-deep .compact-radio-group .el-radio-button__inner {
  background-color: transparent !important;
  border: none !important;
  color: #9CA3AF !important;
  border-radius: 6px !important;
  padding: 8px 20px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
  box-shadow: none !important;
}

::v-deep .compact-radio-group .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: linear-gradient(135deg, #6A33F0 0%, #7b47ff 100%) !important;
  color: #fff !important;
  box-shadow: 0 2px 8px rgba(106, 51, 240, 0.3) !important;
}

::v-deep .compact-radio-group .el-radio-button:first-child .el-radio-button__inner,
::v-deep .compact-radio-group .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 6px !important;
}

::v-deep .compact-radio-group .el-radio-button__inner:hover {
  color: #E5E7EB !important;
}
</style>
